import { Trans } from "@lingui/react/macro"
import { Button, Group } from "@mantine/core"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/**
 * Bottom-of-card action bar for the legacy (Voxcanon) recording panel. Renders
 * a different button set per recording phase:
 *   idle         → Enable Mic (if not monitoring) → Test Mic + Start
 *   recording    → Pause + Finish
 *   paused       → Continue + Finish
 *   finalizing   → nothing (handled by FinalizationProgress)
 *
 * Voxcanon adds a "Scene Insights" button while recording / paused.
 */
export const RecordingActionBar = ({
  state,
  monitoring,
  selectedDevice,
  testRecording,
  testCountdown,
  testAudioUrl,
  product,
  meetingLabel,
  testAudioClassName,
  onEnableMic,
  onStartTestMic,
  onStart,
  onPause,
  onResume,
  onRequestFinish,
  onOpenSceneInsights,
}: IProps): React.JSX.Element => {
  return (
    <Group mb="sm" wrap="wrap">
      {state === "idle" && !monitoring && selectedDevice && (
        <Button variant="light" onClick={onEnableMic}>
          <Trans>Enable microphone</Trans>
        </Button>
      )}
      {state === "idle" && monitoring && (
        <>
          {testRecording ? (
            <Button variant="light" color="danger" disabled size="sm">
              <Trans>Recording test… {testCountdown}s</Trans>
            </Button>
          ) : (
            <Button variant="light" size="sm" onClick={onStartTestMic}>
              <Trans>Test mic (10s)</Trans>
            </Button>
          )}
          {testAudioUrl && !testRecording && (
            // biome-ignore lint/a11y/useMediaCaption: test playback, no transcript needed
            <audio controls src={testAudioUrl} className={`${classes.testAudio} ${testAudioClassName}`} />
          )}
        </>
      )}
      {state === "idle" && (
        <Button color="danger" onClick={onStart} disabled={!selectedDevice}>
          <Trans>Start recording</Trans>
        </Button>
      )}
      {state === "recording" && (
        <>
          <Button color="warning" onClick={onPause}>
            <Trans>Pause</Trans>
          </Button>
          <Button color="brand" onClick={onRequestFinish}>
            <Trans>Finish {meetingLabel}</Trans>
          </Button>
        </>
      )}
      {state === "paused" && (
        <>
          <Button color="danger" onClick={onResume}>
            <Trans>Continue</Trans>
          </Button>
          <Button color="brand" onClick={onRequestFinish}>
            <Trans>Finish {meetingLabel}</Trans>
          </Button>
        </>
      )}
      {product === "voxcanon" && (state === "recording" || state === "paused") && (
        <Button variant="light" color="brand" size="sm" onClick={onOpenSceneInsights}>
          <Trans>Scene insights</Trans>
        </Button>
      )}
    </Group>
  )
}
